import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { AlertController } from '@ionic/angular';
import { from, Observable, switchMap } from 'rxjs';
import { driverMapViewTowDetails$ } from 'src/app/driver/driver-map/driver-map.component';
import { trailerMapShowNotifications$ } from 'src/app/trailer/trailer-map/trailer-map.component';

@Injectable({
    providedIn: 'root'
})
export class UtilityService {
    constructor(
        private alertController: AlertController,
        private router: Router
    ) {}

    prompt(header: string, message: string, acceptBtn: string, denyBtn: string, acceptFn: Function, denyFn?: Function):
        Observable<void> {
        const alert$ = from(this.alertController.create({
            header,
            message,
            buttons: [{
                text: denyBtn,
                role: 'cancel',
                cssClass: 'secondary',
                handler: () => denyFn?.()
            }, {
                text: acceptBtn,
                handler: () => acceptFn()
            }]
        }));
        return alert$.pipe(
            switchMap(alert => alert.present())
        );
    }

    navigate(route: string): void {
        // If the current route is the same as the one we want to navigate to, trigger subjects in the corresponding components
        const currentRoute = this.router.url;
        if (route === currentRoute) {
            if (route.startsWith('/trailer/map/')) {
                trailerMapShowNotifications$.next(true);
            } else if (route.startsWith('/driver/map/')) {
                driverMapViewTowDetails$.next(true);
            }
            return;
        }

        this.router.navigate([route]);
    }

    async handleOnBecomeDriver(handler: () => void): Promise<void> {
        const year = environment.allowedVehiclesMinYear;
        const alert = await this.alertController.create({
            header: 'Vehicle Eligibility Notice',
            subHeader: 'Before creating a driver account, please note:',
            message: `<p>Drive & Tow currently supports pickup trucks and cargo vans (${year}+) for towing and payload
                eligibility.</p>
                <p>Vehicles manufactured prior to ${year} are not eligible at this time due to safety and reliability standards and
                will be declined.</p>
                <p>As demand grows, a future update may allow certain older vehicles to participate in payload-only jobs 
                (truck bed loads, no towing).</p>
                <p>Create an account if you'd like to be notified when this option becomes available.</p>`,
            buttons: [{
                text: 'Cancel',
                role: 'cancel',
                cssClass: 'secondary'
            }, {
                text: 'Continue',
                role: 'confirm',
                handler
            }],
            cssClass: 'vehicle-eligibility-alert'
        });
        await alert.present();
    }

    async handleOnRegister(): Promise<void> {
        if (!environment.disableRegistration) {
            this.router.navigate(['/register']);
            return;
        }

        const alert = await this.alertController.create({
            header: 'New Registrations Temporarily Unavailable',
            message: `Our registration system is under maintenance for the next few days.
                Please check back soon to create your account.
                We appreciate your understanding and patience!`,
            buttons: ['OK']
        });
        await alert.present();
    }
}
